/**
 * SDMS Template Builder — "Data Transfer & Reporting" tab (legacy builder tab
 * "Data Transfer And Reporting", frmSpreadSheetTemplateBuilder): the template's data-transfer
 * workbooks (dbo.SpreadSheetBuilder_DataTransfer) and report layouts
 * (dbo.SpreadSheetBuilder_Reporting), side by side with the legacy columns.
 *
 *  - **Add** a row (legacy: Enter in the grid) and edit it inline — Name; for a report File Type
 *    (DEV / DOC / XLS — locked once the row holds a layout or file, as legacy), SubReport and
 *    ReportSource (RawData / Calibration). These edits are a draft stored with the template's
 *    **Save** (legacy: the rows are saved with the template), via {@link DataTransferReportingHandle}.
 *  - **View** a data transfer → the sheet editor with YAML tools (DataTransferEditorDialog);
 *    a DEV report → the embedded report designer (SdmsReportDesignerDialog); a DOC / XLS report
 *    downloads its file. Both editors save by themselves, as in Windows.
 *  - **Delete** removes the row (an unsaved new row is just dropped).
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { runLoading } from '../../../components/loading';
import { downloadBlob } from '../../../utils/exportData';
import {
  errorText, FILE_TYPES, REPORT_SOURCES, sdmsTemplateApi,
  type DataTransferItem, type OutputKind, type OutputsSave, type ReportItem, type TemplateOutputs,
} from './sdmsTemplateApi';
import { DataTransferEditorDialog, type DataSourceField, type DataTransferTarget } from './DataTransferEditorDialog';
import { SdmsReportDesignerDialog } from './SdmsReportDesignerDialog';

type TransferRow = DataTransferItem & { key: number };
type ReportRow = ReportItem & { key: number };

/** What the editor's Save needs from the tab. */
export interface DataTransferReportingHandle {
  /** The rows added / changed since the last load, or null when there are none. */
  pending: () => OutputsSave | null;
  /** A problem that must block the template's Save (e.g. a row without a Name), or null. */
  problem: () => string | null;
  /** Stores the pending rows; the tab reloads afterwards. */
  save: () => Promise<void>;
}

const MAX_NAME = 50;
/** The legacy tab's grid: the Name column only. */
const LEGACY_TRANSFER_COLS: AppColumn<DataTransferItem & { key: number }>[] = [{ field: 'name', header: 'Name', width: 620 }];
let tempKey = -1;
const fileBase = (name: string | null, fallback: string) => (name?.trim() || fallback).replace(/[\\/:*?"<>|]+/g, '_');
const sameTransfer = (a: DataTransferItem, b: DataTransferItem) => (a.name ?? '') === (b.name ?? '');
const sameReport = (a: ReportItem, b: ReportItem) =>
  (a.name ?? '') === (b.name ?? '') && (a.fileType ?? '') === (b.fileType ?? '')
  && a.subReport === b.subReport && (a.reportSource ?? '') === (b.reportSource ?? '');

export const DataTransferReportingTab = forwardRef<DataTransferReportingHandle, {
  templateId: number | null;
  formName: string;
  canWrite: boolean;
  canDelete: boolean;
  /** Tells the editor whether the tab holds unsaved rows (dirty tracking / unsaved guard). */
  onDirtyChange: (dirty: boolean) => void;
  /** The data-transfer sheet editor's field list (the template's Field Info). */
  dataSourceFields: DataSourceField[];
  /** The legacy (G5) tab, used by Settings ▸ SDMS ▸ SDMS Template Builder: Add · Remove over ONE grid
   *  with a Name column; Add opens the Data Transfer popup. Default false: the Toolbox tab is unchanged. */
  legacy?: boolean;
  /** Legacy only: the popup's CalibrationData list. */
  calibrationFields?: DataSourceField[];
}>(function DataTransferReportingTab({ templateId, formName, canWrite, canDelete, onDirtyChange, dataSourceFields, legacy = false, calibrationFields = [] }, ref) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runDelete = useDeleteFlow();
  const [saved, setSaved] = useState<TemplateOutputs>({ dataTransfers: [], reports: [] });
  const [transfers, setTransfers] = useState<TransferRow[]>([]);
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<(DataTransferTarget & { key: number }) | null>(null);
  const [designing, setDesigning] = useState<{ uqID: number; name: string } | null>(null);
  /** Legacy tab: the ticked rows Remove acts on. */
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());

  /** Loads the lists. With `keepDraft` (after a popup saved one row) the other rows' unsaved
   *  edits and new rows survive — only `drop` (the row the popup stored) is replaced. */
  const load = useCallback(async (keepDraft = false, drop?: number) => {
    if (!templateId) { setSaved({ dataTransfers: [], reports: [] }); setTransfers([]); setReports([]); return; }
    setLoading(true);
    try {
      const data = await sdmsTemplateApi.outputs(templateId);
      setSaved(data);
      setTransfers(prev => {
        const fresh = data.dataTransfers.map(t => ({ ...t, key: t.uqID }));
        if (!keepDraft) return fresh;
        const edited = new Map(prev.filter(p => p.uqID > 0 && p.key !== drop).map(p => [p.uqID, p]));
        return [...fresh.map(t => (edited.has(t.uqID) ? { ...t, name: edited.get(t.uqID)!.name } : t)),
          ...prev.filter(p => p.uqID <= 0 && p.key !== drop)];
      });
      setReports(prev => {
        const fresh = data.reports.map(r => ({ ...r, key: r.uqID }));
        if (!keepDraft) return fresh;
        const edited = new Map(prev.filter(p => p.uqID > 0).map(p => [p.uqID, p]));
        return [...fresh.map(r => { const d = edited.get(r.uqID); return d ? { ...r, name: d.name, fileType: d.fileType, subReport: d.subReport, reportSource: d.reportSource } : r; }),
          ...prev.filter(p => p.uqID <= 0)];
      });
    } catch (e) {
      notify.error(errorText(e, 'Could not load the data transfers and reports.'));
    } finally {
      setLoading(false);
    }
  }, [templateId, notify]);

  useEffect(() => { void load(); }, [load]);

  const pending = useCallback((): OutputsSave | null => {
    const t = transfers.filter(r => {
      const o = saved.dataTransfers.find(x => x.uqID === r.uqID);
      return r.uqID <= 0 || !o || !sameTransfer(o, r);
    }).map(r => ({ uqID: Math.max(r.uqID, 0), name: (r.name ?? '').trim() }));
    const rep = reports.filter(r => {
      const o = saved.reports.find(x => x.uqID === r.uqID);
      return r.uqID <= 0 || !o || !sameReport(o, r);
    }).map(r => ({ uqID: Math.max(r.uqID, 0), name: (r.name ?? '').trim(), fileType: r.fileType ?? '', subReport: r.subReport, reportSource: r.reportSource || null }));
    return t.length || rep.length ? { transfers: t, reports: rep } : null;
  }, [transfers, reports, saved]);

  const dirty = useMemo(() => pending() != null, [pending]);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const problem = useCallback((): string | null => {
    const p = pending();
    if (!p) return null;
    for (const t of p.transfers) {
      if (!t.name) return 'Data Transfer & Reporting: every data transfer needs a Name.';
      if (t.name.length > MAX_NAME) return `Data Transfer & Reporting: a Name must be ${MAX_NAME} characters or fewer.`;
    }
    for (const r of p.reports) {
      if (!r.name) return 'Data Transfer & Reporting: every report needs a Name.';
      if (r.name.length > MAX_NAME) return `Data Transfer & Reporting: a Name must be ${MAX_NAME} characters or fewer.`;
      if (!r.fileType) return `Data Transfer & Reporting: choose a File Type for report "${r.name}".`;
    }
    return null;
  }, [pending]);

  useImperativeHandle(ref, () => ({
    pending,
    problem,
    save: async () => {
      const p = pending();
      if (!templateId || !p) return;
      await sdmsTemplateApi.saveOutputs(templateId, p);
      await load();
    },
  }), [pending, problem, templateId, load]);

  // ── add / edit ──────────────────────────────────────────────────────────
  const addTransfer = () => setTransfers(rows => [...rows,
    { key: tempKey--, uqID: 0, name: '', hasFile: false, hasYaml: false, modifiedDate: null }]);
  const addReport = () => setReports(rows => [...rows,
    { key: tempKey--, uqID: 0, name: '', fileType: 'DEV', subReport: false, reportSource: 'RawData', hasFile: false, usesLegacyLayout: false, modifiedDate: null }]);

  const commitTransfer = (row: TransferRow, _col: AppColumn<TransferRow>, v: string) =>
    setTransfers(rows => rows.map(r => (r.key === row.key ? { ...r, name: v.slice(0, MAX_NAME) } : r)));
  const commitReport = (row: ReportRow, col: AppColumn<ReportRow>, v: string) =>
    setReports(rows => rows.map(r => {
      if (r.key !== row.key) return r;
      if (col.field === 'name') return { ...r, name: v.slice(0, MAX_NAME) };
      if (col.field === 'fileType') return { ...r, fileType: v };
      if (col.field === 'reportSource') return { ...r, reportSource: v || null };
      return r;
    }));

  // ── view ────────────────────────────────────────────────────────────────
  const viewTransfer = (r: TransferRow) => {
    if (!templateId) return;
    if (!(r.name ?? '').trim()) { void alert({ title: 'Data Transfer', message: 'Enter a Name for the data transfer first.', tone: 'warning' }); return; }
    setEditing({ key: r.key, uqID: Math.max(r.uqID, 0), name: (r.name ?? '').trim(), hasFile: r.hasFile, hasYaml: r.hasYaml });
  };
  const viewReport = async (r: ReportRow) => {
    if (!templateId) return;
    const stored = saved.reports.find(x => x.uqID === r.uqID);
    if (r.uqID <= 0 || !stored || (stored.fileType ?? '') !== (r.fileType ?? '')) {
      await alert({ title: 'Report', message: 'Save the template first — the report row (with its File Type) must be stored before it can be viewed.', tone: 'info' });
      return;
    }
    if (r.fileType === 'DEV') {
      // A Windows-builder (legacy) layout opens as it is — the designer host converts it in memory.
      setDesigning({ uqID: r.uqID, name: r.name ?? '' });
      return;
    }
    if (!r.hasFile) { await alert({ title: 'Report', message: `${r.fileType} reports are designed in the Windows SDMS Template Builder; this row has no file yet.`, tone: 'info' }); return; }
    try {
      const blob = await runLoading(sdmsTemplateApi.outputFile(templateId, 'report', r.uqID), { label: 'Opening…' });
      downloadBlob(blob, `${fileBase(r.name, `Report-${r.uqID}`)}${r.fileType === 'XLS' ? '.xlsx' : '.rtf'}`);
    } catch (e) {
      notify.error(errorText(e, 'This report has no file.'));
    }
  };

  // ── delete ──────────────────────────────────────────────────────────────
  const remove = async (kind: OutputKind, key: number, uqID: number, name: string | null) => {
    const label = kind === 'transfer' ? 'data transfer' : 'report';
    if (uqID <= 0) {   // not stored yet — just drop it (legacy drFocused.Delete)
      if (!(await confirm({ title: 'Delete', message: `Delete the new ${label}${name ? ` "${name}"` : ''}?`, tone: 'danger' }))) return;
      if (kind === 'transfer') setTransfers(rows => rows.filter(r => r.key !== key));
      else setReports(rows => rows.filter(r => r.key !== key));
      return;
    }
    if (!templateId) return;
    await runDelete({
      formName,
      message: `Delete the ${label} "${name ?? uqID}" from this template?`,
      delete: async () => { await sdmsTemplateApi.removeOutput(templateId, kind, uqID); },
      onError: e => notify.error(errorText(e, 'Delete failed.')),
      onDeleted: () => {
        // Drop the row, keep the draft of the others.
        if (kind === 'transfer') { setTransfers(rows => rows.filter(r => r.key !== key)); setSaved(s => ({ ...s, dataTransfers: s.dataTransfers.filter(d => d.uqID !== uqID) })); }
        else { setReports(rows => rows.filter(r => r.key !== key)); setSaved(s => ({ ...s, reports: s.reports.filter(d => d.uqID !== uqID) })); }
      },
    });
  };

  const link = (label: string, onClick: () => void, disabled = false) => (
    <button type="button" className="stb-link" disabled={disabled} onClick={e => { e.stopPropagation(); onClick(); }}>{label}</button>
  );
  const hasLayout = (r: ReportRow) => r.hasFile && r.uqID > 0;

  const transferCols: AppColumn<TransferRow>[] = [
    { field: 'name', header: 'Name', width: 220, edit: canWrite ? { type: 'text' } : undefined },
    { field: 'yaml', header: 'YAML', width: 60, align: 'center', sortable: false, filterable: false, noExport: true,
      render: r => (r.hasYaml ? <i className="ti ti-file-code" title="An instrument-parser YAML is stored" /> : null) },
    { field: 'view', header: 'View', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => link('View', () => viewTransfer(r)) },
    { field: 'delete', header: 'Delete', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => link('Delete', () => void remove('transfer', r.key, r.uqID, r.name), !canDelete && r.uqID > 0) },
  ];
  const reportCols: AppColumn<ReportRow>[] = [
    { field: 'name', header: 'Name', width: 190, edit: canWrite ? { type: 'text' } : undefined },
    { field: 'fileType', header: 'File Type', width: 90,
      edit: canWrite ? { type: 'combo', options: [...FILE_TYPES], disabled: r => hasLayout(r) } : undefined },
    { field: 'view', header: 'View', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => link('View', () => void viewReport(r)) },
    { field: 'delete', header: 'Delete', width: 70, sortable: false, filterable: false, noExport: true,
      render: r => link('Delete', () => void remove('report', r.key, r.uqID, r.name), !canDelete && r.uqID > 0) },
    { field: 'subReport', header: 'SubReport', width: 85, align: 'center', text: r => (r.subReport ? 'Yes' : 'No'), value: r => (r.subReport ? 'Yes' : 'No'),
      render: r => (
        <input type="checkbox" checked={r.subReport} disabled={!canWrite} aria-label={`SubReport — ${r.name ?? ''}`}
          onClick={e => e.stopPropagation()}
          onChange={e => { const v = e.target.checked; setReports(rows => rows.map(x => (x.key === r.key ? { ...x, subReport: v } : x))); }} />
      ) },
    { field: 'reportSource', header: 'ReportSource', width: 120,
      edit: canWrite ? { type: 'combo', options: ['', ...REPORT_SOURCES], clearable: true } : undefined },
  ];

  if (legacy) {
    // Add opens the popup straight away; the row is created when the popup saves (its Name box).
    const addLegacy = () => {
      if (!templateId) { void alert({ title: 'Data Transfer', message: 'Save the template first — a data transfer belongs to a saved template.', tone: 'info' }); return; }
      setEditing({ key: tempKey--, uqID: 0, name: '', hasFile: false, hasYaml: false });
    };
    const removeTicked = async () => {
      for (const r of transfers.filter(t => ticked.has(t.key))) await remove('transfer', r.key, r.uqID, r.name);
      setTicked(new Set());
    };
    return (
      <div className="stb-outputs-legacy">
        <div className="stb-actions">
          <button type="button" className="ca-btn" disabled={!canWrite} onClick={addLegacy}><i className="ti ti-plus" /> Add</button>
          <button type="button" className="ca-btn" disabled={!canDelete || ticked.size === 0} onClick={() => void removeTicked()}>
            <i className="ti ti-trash" /> Remove</button>
        </div>
        <AppDataGrid<TransferRow> rows={transfers} columns={LEGACY_TRANSFER_COLS} getRowId={r => r.key}
          loading={loading} selectable selectedIds={ticked} onSelectionChange={setTicked}
          onRowDoubleClick={viewTransfer} defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
        {templateId != null && templateId > 0 && (
          <DataTransferEditorDialog legacy templateId={templateId} target={editing} editable={canWrite} formName={formName}
            fields={dataSourceFields} calibrationFields={calibrationFields}
            onClose={() => setEditing(null)}
            onSaved={() => { void load(true, editing?.key); }} />
        )}
      </div>
    );
  }

  if (!templateId) return <p className="stb-hint">Save the template first — data transfers and reports belong to a saved template.</p>;

  return (
    <div className="stb-outputs">
      <section className="stb-outputs-pane">
        <div className="stb-outputs-head">
          <h4>Data Transfer</h4>
          {canWrite && <button type="button" className="ca-btn" onClick={addTransfer}><i className="ti ti-plus" /> Add</button>}
        </div>
        <AppDataGrid<TransferRow> rows={transfers} columns={transferCols} getRowId={r => r.key}
          loading={loading} defaultPageSize={10} maximizable exportFilename="SDMS-Data-Transfer"
          onCellCommit={canWrite ? commitTransfer : undefined} emptyText="No data transfers." />
      </section>
      <section className="stb-outputs-pane">
        <div className="stb-outputs-head">
          <h4>Reporting</h4>
          {canWrite && <button type="button" className="ca-btn" onClick={addReport}><i className="ti ti-plus" /> Add</button>}
        </div>
        <AppDataGrid<ReportRow> rows={reports} columns={reportCols} getRowId={r => r.key}
          loading={loading} defaultPageSize={10} maximizable exportFilename="SDMS-Reporting"
          onCellCommit={canWrite ? commitReport : undefined} emptyText="No reports." />
      </section>
      {dirty && <p className="stb-hint stb-outputs-note">New and changed rows are stored when you Save the template.</p>}

      <DataTransferEditorDialog templateId={templateId} target={editing} editable={canWrite} formName={formName}
        fields={dataSourceFields}
        onClose={() => setEditing(null)}
        onSaved={() => { void load(true, editing?.key); }} />
      <SdmsReportDesignerDialog templateId={templateId} report={designing} onClose={() => { setDesigning(null); void load(true); }} />
    </div>
  );
});
